import React, { useEffect, useMemo, useState } from "react";
import Layout from "../Layout/Layout";
import "../css/power-score.css";
import DoughnutChart from "../Components/DoughnutChart";
import BarChart from "../Components/Barchart";
import LineChart from "../Components/LineChart";
import StatsCard from "../Components/StatsCard";
import { useGetLevelDetails } from "../Controllers/DashboardController";
import { useGetPowerStats } from "../Controllers/PowerStatController";
import CardSkeletonLoader from "../Components/SkeletonLoaders/CardSkeletonLoader";
import CircularSkeletonLoader from "../Components/SkeletonLoaders/CircularSkeletonLoader";
import PMMonthlyBarChart from "../Components/PMMonthlyBarChart";

export default function PowerScore() {
  const [filterType, setFilterType] = useState("weekly");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [displayPeriod, setDisplayPeriod] = useState(new Date());

  // Format date to YYYY-MM-DD
  const formatDateToAPI = (date) => {
    if (!date) return "";
    return date.toISOString().split("T")[0];
  };

  // Calculate the end date based on the start date and filter type
  const getEndDate = (startDate, type) => {
    const endDate = new Date(startDate);
    if (type === "weekly") {
      endDate.setDate(startDate.getDate() + 6);
    } else if (type === "monthly") {
      endDate.setMonth(startDate.getMonth() + 1);
      endDate.setDate(0);
    } else if (type === "yearly") {
      endDate.setFullYear(startDate.getFullYear() + 1);
      endDate.setDate(0);
    }
    return endDate;
  };

  const {
    data: userLevelData,
    isLoading: isUserLevelLoading,
    error: userLevelError,
    refetch: refetchUserLevel,
  } = useGetLevelDetails();

  // Calculate query parameters with proper dependency tracking
  const queryParams = useMemo(() => {
    const startDate = new Date(currentDate);

    // Adjust start date based on filter type
    if (filterType === "weekly") {
      const dayOfWeek = startDate.getDay();
      const diff = startDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      startDate.setDate(diff);
    } else if (filterType === "monthly") {
      startDate.setDate(1);
    } else if (filterType === "yearly") {
      startDate.setMonth(0, 1);
    }

    return {
      from_date: formatDateToAPI(startDate),
      to_date: formatDateToAPI(getEndDate(startDate, filterType)),
      filter_type: filterType,
    };
  }, [currentDate, filterType]);

  const {
    data: powerStatsData,
    isLoading: isPowerStatsLoading,
    error: powerStatsError,
    refetch: refetchPowerStats,
  } = useGetPowerStats(queryParams);

  const powerStat = powerStatsData ? powerStatsData : null;
  const userLevel = userLevelData ? userLevelData?.level_details : null;

  // This useEffect will run on initial load and whenever filterType or currentDate changes
  useEffect(() => {
    const startDate = new Date(currentDate);

    if (filterType === "weekly") {
      const dayOfWeek = startDate.getDay();
      const diff = startDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      startDate.setDate(diff);
    } else if (filterType === "monthly") {
      startDate.setDate(1);
    } else if (filterType === "yearly") {
      startDate.setMonth(0, 1);
    }

    setDisplayPeriod(startDate);
  }, [filterType, currentDate]);

  // Refetch both power stats and user level data when filters change to ensure we have the most up-to-date information
  useEffect(() => {
    // Debounce the refetch to avoid excessive API calls
    const timeoutId = setTimeout(() => {
      refetchPowerStats();
      refetchUserLevel();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [filterType, currentDate, refetchPowerStats, refetchUserLevel]);

  // Handle filter type change from the dropdown
  const handleFilterChange = (e) => {
    const newFilterType = e.target.value.toLowerCase();
    setFilterType(newFilterType);
    setCurrentDate(new Date());
  };

  // Handle navigation for prev/next buttons
  const handleNavigation = (direction) => {
    const newDate = new Date(currentDate);

    if (filterType === "weekly") {
      newDate.setDate(newDate.getDate() + (direction === "prev" ? -7 : 7));
    } else if (filterType === "monthly") {
      newDate.setMonth(newDate.getMonth() + (direction === "prev" ? -1 : 1));
    } else if (filterType === "yearly") {
      newDate.setFullYear(
        newDate.getFullYear() + (direction === "prev" ? -1 : 1),
      );
    }

    setCurrentDate(newDate);
  };

  // Display the formatted date range
  const formatDateDisplay = (startDate) => {
    if (!startDate) return "";

    const endDate = getEndDate(startDate, filterType);
    if (filterType === "yearly") {
      return `${startDate.getFullYear()}`;
    } else if (filterType === "monthly") {
      const options = { month: "long", year: "numeric" };
      return startDate.toLocaleDateString("en-US", options);
    } else {
      const options = { day: "2-digit", month: "short" };
      const start = startDate.toLocaleDateString("en-US", options);
      const end = endDate.toLocaleDateString("en-US", options);
      const year = endDate.getFullYear();
      return `${start} - ${end}, ${year}`;
    }
  };

  const getDoughnutChartData = () => {
    if (!powerStat) return [];
    return [
      {
        label: "Goals",
        data: [
          parseFloat(powerStat.average_goal_percentage) || 0,
          100 - (parseFloat(powerStat.average_goal_percentage) || 0),
        ],
        growthPercentage:
          parseFloat(powerStat.average_growth_goal_percentage) || 0,
        backgroundColor: ["#008CFF", "#1C2934"],
        borderWidth: 0,
        borderColor: "transparent",
        circumference: 360,
        rotation: 85,
        weight: 4,
        borderRadius: [20, 20],
        cutout: "87.34%",
      },
      {
        label: "Task",
        data: [
          parseFloat(powerStat.average_tasks_percentage) || 0,
          100 - (parseFloat(powerStat.average_tasks_percentage) || 0),
        ],
        growthPercentage:
          parseFloat(powerStat.average_growth_tasks_percentage) || 0,
        backgroundColor: ["#F5CA41", "#1C2934"],
        borderWidth: 0,
        borderColor: "transparent",
        circumference: 360,
        weight: 2,
        rotation: 100,
        borderRadius: [20, 20],
        cutout: "75.26%",
      },
      {
        label: "Habit",
        data: [
          parseFloat(powerStat.average_habits_percentage) || 0,
          100 - (parseFloat(powerStat.average_habits_percentage) || 0),
        ],
        growthPercentage:
          parseFloat(powerStat.average_growth_habits_percentage) || 0,
        backgroundColor: ["#66BAFF", "#1C2934"],
        borderWidth: 0,
        borderColor: "transparent",
        circumference: 360,
        rotation: 110,
        weight: 1.2,
        borderRadius: [20, 20],
        cutout: "64.7%",
      },
      {
        label: "Journal",
        data: [
          parseFloat(powerStat.average_journals_percentage) || 0,
          100 - (parseFloat(powerStat.average_journals_percentage) || 0),
        ],
        growthPercentage:
          parseFloat(powerStat.average_growth_journals_percentage) || 0,
        backgroundColor: ["#5CE0A0", "#1C2934"],
        borderWidth: 0,
        borderColor: "transparent",
        circumference: 360,
        rotation: 110,
        weight: 1,
        borderRadius: [20, 20],
        cutout: "55%",
      },
    ];
  };

  const barChartData = useMemo(() => {
    if (!powerStat) return [];
    const weekdays = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
    const weekly = powerStat?.weeklystatus || {};

    return [
      {
        label: "Journal",
        data: weekdays.map((day) => weekly.journal?.[day] || 0),
        backgroundColor: "#5CE0A0",
        stack: "stack1",
        borderWidth: 1,
        borderColor: "#121E29",
      },
      {
        label: "Habit",
        data: weekdays.map((day) => weekly.habits?.[day] || 0),
        backgroundColor: "#66BAFF",
        stack: "stack1",
        borderWidth: 1,
        borderColor: "#121E29",
      },
      {
        label: "Task",
        data: weekdays.map((day) => weekly.tasks?.[day] || 0),
        backgroundColor: "#F5CA41",
        stack: "stack1",
        borderWidth: 1,
        borderColor: "#121E29",
      },
      {
        label: "Goals",
        data: weekdays.map((day) => weekly.goals?.[day] || 0),
        backgroundColor: "#008CFF",
        stack: "stack1",
        borderWidth: 1,
        borderColor: "#121E29",
      },
    ];
  }, [powerStat]);

  const getLineChartData = () => {
    if (!powerStat) return [];
    const months = [
      "jan",
      "feb",
      "mar",
      "apr",
      "may",
      "jun",
      "jul",
      "aug",
      "sep",
      "oct",
      "nov",
      "dec",
    ];
    const monthlyStatus = powerStat?.monthlystatus || {
      journal: {},
      habits: {},
      tasks: {},
      goals: {},
    };
    const journalData = months.map(
      (month) => monthlyStatus.journal[month] || 0,
    );
    const habitsData = months.map((month) => monthlyStatus.habits[month] || 0);
    const taskData = months.map((month) => monthlyStatus.tasks[month] || 0);
    const goalData = months.map((month) => monthlyStatus.goals[month] || 0);

    return [
      {
        label: "Journal",
        data: journalData,
        borderColor: "#5CE0A0",
        borderWidth: 2,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, "rgba(92, 224, 160, 0.2)");
          gradient.addColorStop(0.5, "rgba(92, 224, 160, 0.1)");
          gradient.addColorStop(1, "rgba(92, 224, 160, 0)");
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
      {
        label: "Habit",
        data: habitsData,
        borderColor: "#66BAFF",
        borderWidth: 2,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, "rgba(102, 186, 255, 0.2)");
          gradient.addColorStop(0.5, "rgba(102, 186, 255, 0.1)");
          gradient.addColorStop(1, "rgba(102, 186, 255, 0)");
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
      {
        label: "Task",
        data: taskData,
        borderColor: "#F5CA41",
        borderWidth: 2,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, "rgba(245, 202, 65, 0.2)");
          gradient.addColorStop(0.5, "rgba(245, 202, 65, 0.1)");
          gradient.addColorStop(1, "rgba(245, 202, 65, 0)");
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
      {
        label: "Goals",
        data: goalData,
        borderColor: "#008CFF",
        borderWidth: 2,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, "rgba(0, 140, 255, 0.2)");
          gradient.addColorStop(0.5, "rgba(0, 140, 255, 0.1)");
          gradient.addColorStop(1, "rgba(0, 140, 255, 0)");
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ];
  };

  const getTrendProps = (value) => ({
    trendIcon:
      value >= 0
        ? "/icons/graph-score-increase.svg"
        : "/icons/graph-score-decrease.svg",
    chartIcon:
      value >= 0
        ? "/icons/assesssment-chart-increase.svg"
        : "/icons/assessment-chart-decrease.svg",
    percentageColor: value >= 0 ? "#5ce0a0" : "#ff6b6b",
  });

  const pmMonthlyTrends = useMemo(() => {
    const src = powerStat?.yearlyPmReflectionScores || [];

    const monthOrder = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const normalizeScores = (scores) =>
      scores && typeof scores === "object" && !Array.isArray(scores)
        ? scores
        : {};

    // Map: "Jan" -> { energy_score_average: 3, ... }
    const byMonth = new Map(src.map((m) => [m.key, normalizeScores(m.scores)]));

    const makeSeries = (field) =>
      monthOrder.map((mon) => {
        const v = byMonth.get(mon)?.[field];
        return typeof v === "number" ? v : Number(v) || 0; // handles "3" too
      });

    const metrics = [
      { title: "Energy Score", field: "energy_score_average" },
      { title: "Peace Score", field: "peace_score_average" },
      { title: "Purpose Score", field: "purpose_score_average" },
      { title: "Presence Score", field: "presence_score_average" },
      { title: "Productivity Score", field: "productivity_score_average" },
      { title: "Joy Score", field: "joy_score_average" },
      { title: "Connection Score", field: "connection_score_average" },
      { title: "Overall Score", field: "overall_score_average" },
    ].map((m) => ({ ...m, data: makeSeries(m.field) }));

    return { labels: monthOrder, metrics, maxValue: 8 };
  }, [powerStat?.yearlyPmReflectionScores]);

  const habitTrend = powerStat
    ? getTrendProps(powerStat.user_growth_habits)
    : {};
  const journalTrend = powerStat
    ? getTrendProps(powerStat.user_journals_habits)
    : {};
  const goalTrend = powerStat ? getTrendProps(powerStat.user_goals_habits) : {};
  const taskTrend = powerStat ? getTrendProps(powerStat.user_tasks_habits) : {};
  const warmapTrend = powerStat
    ? getTrendProps(powerStat.user_warmap_percentage)
    : {};
  const gameTrend = powerStat
    ? getTrendProps(powerStat.user_game_percentage)
    : {};

  // Combined loading state
  const isLoading = isPowerStatsLoading || isUserLevelLoading || !powerStat;

  if (isLoading) {
    return (
      <Layout>
        <div
          className="page-content"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <div className="container py-4">
            <div className="row g-3 mb-4">
              <div className="col-12 col-md-6">
                <CardSkeletonLoader width="100%" height="200px" />
              </div>
              <div className="col-12 col-md-6">
                <CardSkeletonLoader width="100%" height="200px" />
              </div>
              <div className="col-12 col-md-6">
                <CardSkeletonLoader width="100%" height="200px" />
              </div>
              <div className="col-12 col-md-6">
                <CardSkeletonLoader width="100%" height="200px" />
              </div>
            </div>

            <div className="row g-3">
              <div className="col-6 col-md-3">
                <CircularSkeletonLoader size="140px" />
              </div>
              <div className="col-6 col-md-3">
                <CircularSkeletonLoader size="140px" />
              </div>
              <div className="col-6 col-md-3">
                <CircularSkeletonLoader size="140px" />
              </div>
              <div className="col-6 col-md-3">
                <CircularSkeletonLoader size="140px" />
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content">
        <div className="setting-content py-4">
          <h1
            className="text-heading-h2"
            style={{ color: "#e6ecf1", marginBottom: "12px" }}
          >
            Power Score Stats
          </h1>

          <div className="d-flex justify-content-between mb-4 position-relative power-week-display-container">
            <div className="level-content mt-0" style={{ width: "413px" }}>
              <div className="level-info d-flex justify-content-between align-items-center">
                <span className="text-body-b1">
                  Level {userLevel?.current_level}
                </span>
                <span className="text-body-b3">
                  Needs {userLevel?.point_remaining_for_next_level} points to
                  level {userLevel?.next_level}
                </span>
              </div>
              <div className="progress-wrapper mt-0 w-100">
                <div className="progress-bar-wrapper">
                  <div
                    className="progress-bar-fill"
                    style={{
                      width: `${userLevel?.progress_percentage}%`,
                    }}
                  ></div>
                </div>
                <div className="points-display d-flex justify-content-between">
                  <span className="text-body-b3">
                    {userLevel?.total_points_of_user}
                  </span>
                  <span className="text-body-b3">
                    {userLevel?.total_points_for_next_level}
                  </span>
                </div>
              </div>
            </div>

            <div
              className="d-flex align-items-center selectedWeek"
              style={{ gap: "20px" }}
            >
              <button
                id="prevButton"
                className="btn d-flex justify-content-center align-items-center rounded-circle"
                style={{
                  backgroundColor: "#1c2934",
                  width: "40px",
                  height: "40px",
                }}
                onClick={() => handleNavigation("prev")}
              >
                <img src="/icons/pagination-left-icon.svg" alt="" />
              </button>
              <span
                style={{ color: "#c8ced3" }}
                className="text-body-b3 week-date-score"
              >
                {formatDateDisplay(displayPeriod)}
              </span>
              <button
                id="nextButton"
                className="btn d-flex justify-content-center align-items-center rounded-circle"
                style={{
                  backgroundColor: "#1c2934",
                  width: "40px",
                  height: "40px",
                }}
                onClick={() => handleNavigation("next")}
              >
                <img src="/icons/pagination-right-icon.svg" alt="" />
              </button>
            </div>

            <div className="power-stat-assess">
              <select
                className="form-select text-body-b3"
                style={{
                  appearance: "none",
                  background:
                    "url(/icons/drop-down-score.svg) no-repeat right 12px center",
                  backgroundSize: "14px 14px",
                  borderRadius: "12px",
                  border: "1px solid #1b2c3a",
                  padding: "5px 12px",
                  height: "36px",
                  width: "98px",
                  color: "#c8ced3",
                  backgroundColor: "#121e29",
                }}
                value={filterType}
                onChange={handleFilterChange}
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
          </div>
          <div className="row score-stat-main-row">
            <div className="col-12">
              {/* stats cards  */}
              <div className="row score-stat-top-row">
                <StatsCard
                  icon="/icons/habits-icon.svg"
                  label="User Habit"
                  score={powerStat.user_habits}
                  total={powerStat.total_habits}
                  percentage={powerStat.user_growth_habits}
                  {...habitTrend}
                  scoreColor="#e6ecf1"
                />
                <StatsCard
                  icon="/icons/journal-icon.svg"
                  label="User Journal"
                  score={powerStat.user_journals}
                  total={powerStat.total_journals}
                  percentage={powerStat.user_journals_habits}
                  {...journalTrend}
                  scoreColor="#e6ecf1"
                />
                <StatsCard
                  icon="/icons/power-score.svg"
                  label="Total Goals"
                  score={powerStat.user_total_goals}
                  total={powerStat.total_goals}
                  percentage={powerStat.user_goals_habits}
                  {...goalTrend}
                  scoreColor="#e6ecf1"
                />
                <StatsCard
                  icon="/icons/task-icon.svg"
                  label="User Task"
                  score={powerStat.user_tasks}
                  total={powerStat.total_tasks}
                  percentage={powerStat.user_tasks_habits}
                  {...taskTrend}
                  scoreColor="#e6ecf1"
                />
                <StatsCard
                  icon="/icons/task-icon.svg"
                  label="War Map"
                  score={powerStat.user_warmap_completed}
                  total={powerStat.user_warmap_total}
                  percentage={powerStat.user_warmap_percentage}
                  {...warmapTrend}
                  scoreColor="#e6ecf1"
                />
                <StatsCard
                  icon="/icons/task-icon.svg"
                  label="Game"
                  score={powerStat.user_game_completed}
                  total={powerStat.user_game_total}
                  percentage={powerStat.user_game_percentage}
                  {...gameTrend}
                  scoreColor="#e6ecf1"
                />
              </div>
            </div>
            <div className="col-12">
              <div className="row score-stat-top-row average-scores-row">
                <div className="col-lg-6 col-xl-3">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      height: "96px",
                      backgroundColor: "#121e29",
                      padding: "16px",
                      border: "1px solid #1b2c3a",
                      borderRadius: "20px",
                    }}
                  >
                    <div className="average-scores-card">
                      {powerStat.previous_week_average}
                    </div>
                    <p
                      className="text-body-b3 mb-0"
                      style={{ color: "#b5bbc0", marginLeft: "16px" }}
                    >
                      Previous Week
                    </p>
                  </div>
                </div>
                <div className="col-lg-6 col-xl-3">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      height: "96px",
                      backgroundColor: "#121e29",
                      padding: "16px",
                      border: "1px solid #1b2c3a",
                      borderRadius: "20px",
                    }}
                  >
                    <div className="average-scores-card">
                      {powerStat.four_week_average}
                    </div>
                    <p
                      className="text-body-b3 mb-0"
                      style={{ color: "#b5bbc0", marginLeft: "16px" }}
                    >
                      4 Week Average
                    </p>
                  </div>
                </div>
                <div className="col-lg-6 col-xl-3">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      height: "96px",
                      backgroundColor: "#121e29",
                      padding: "16px",
                      border: "1px solid #1b2c3a",
                      borderRadius: "20px",
                    }}
                  >
                    <div className="average-scores-card">
                      {powerStat.year_average}
                    </div>
                    <p
                      className="text-body-b3 mb-0"
                      style={{ color: "#b5bbc0", marginLeft: "16px" }}
                    >
                      Year Average
                    </p>
                  </div>
                </div>
                <div className="col-lg-6 col-xl-3">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      height: "96px",
                      backgroundColor: "#121e29",
                      padding: "16px",
                      border: "1px solid #1b2c3a",
                      borderRadius: "20px",
                    }}
                  >
                    <div className="average-scores-card">
                      {powerStat.overall_average}
                    </div>
                    <p
                      className="text-body-b3 mb-0"
                      style={{ color: "#b5bbc0", marginLeft: "16px" }}
                    >
                      Overall Average
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* PM Reflection Score Averages Card */}
            {powerStat?.pm_reflectionscore_averages && (
              <div className="col-12">
                <div className="journal-card p-3 p-md-4 mb-4">
                  <h3
                    className="text-heading-h3 mb-4"
                    style={{ color: "#e6ecf1" }}
                  >
                    Average Life Score
                  </h3>
                  <div className="row g-3">
                    {Object.entries(powerStat.pm_reflectionscore_averages).map(
                      ([key, value]) => {
                        // Convert snake_case to Title Case and remove 'average' if present
                        const words = key.split("_");
                        const filteredWords = words.filter(
                          (word) => word.toLowerCase() !== "average",
                        );
                        const formattedKey = filteredWords
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1),
                          )
                          .join(" ");

                        return (
                          <div key={key} className="col-12 col-md-4 col-lg-3">
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "start",
                                height: "80px",
                                backgroundColor: "#1c2934",
                                padding: "12px",
                                border: "1px solid #2a3a4a",
                                borderRadius: "12px",
                              }}
                            >
                              <div
                                className="average-scores-card"
                                style={{
                                  fontSize: "1.2rem",
                                  fontWeight: "600",
                                }}
                              >
                                {value}
                              </div>
                              <p
                                className="text-body-b3 mb-0"
                                style={{
                                  color: "#b5bbc0",
                                  marginLeft: "12px",
                                  fontSize: "0.9rem",
                                }}
                              >
                                {formattedKey}
                              </p>
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              </div>
            )}

            {pmMonthlyTrends?.metrics?.length > 0 && (
              <div className="col-12">
                <div className="journal-card p-3 p-md-4 mb-4">
                  <h3
                    className="text-heading-h3 mb-4"
                    style={{ color: "#e6ecf1" }}
                  >
                    Monthly Trends
                  </h3>

                  <div className="row g-3">
                    {pmMonthlyTrends.metrics.map((m) => (
                      <div key={m.field} className="col-12 col-lg-6">
                        <div
                          style={{
                            height: "300px",
                            backgroundColor: "#121e29",
                            border: "1px solid #1b2c3a",
                            borderRadius: "20px",
                            padding: "16px",
                          }}
                        >
                          <PMMonthlyBarChart
                            title={m.title}
                            labels={pmMonthlyTrends.labels}
                            data={m.data}
                            maxValue={pmMonthlyTrends.maxValue}
                            barColor="#008CFF"
                            containerStyle={{ height: "100%", width: "100%" }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="col-12">
              <div className="row g-3 g-md-4 charts-row">
                <div className="col-lg-12 col-xl-4">
                  <div className="journal-card metric-card">
                    <h3 className="text-heading-h3 mb-4">Average Score</h3>

                    <div className="chart-container">
                      <DoughnutChart
                        datasets={getDoughnutChartData()}
                        width={206}
                        height={206}
                      />

                      <div className="score-value">
                        <span
                          className="text-heading-h2"
                          style={{ color: "var(--Text-50, #e6ecf1)" }}
                        >
                          {powerStat.average_total_percentage}%
                        </span>
                        <div
                          className="d-flex align-items-center"
                          style={{ gap: "6px" }}
                        >
                          <img
                            src={
                              parseFloat(
                                powerStat.average_total_growth_percentage,
                              ) >= 0
                                ? "/icons/assesssment-chart-increase.svg"
                                : "/icons/assessment-chart-decrease.svg"
                            }
                            alt=""
                          />
                          <span
                            className="text-body-b3"
                            style={{
                              color:
                                parseFloat(
                                  powerStat.average_total_growth_percentage,
                                ) >= 0
                                  ? "#5ce0a0"
                                  : "#ff4d4f",
                            }}
                          >
                            {powerStat.average_total_growth_percentage}%
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="legend-container">
                      <div
                        className="d-flex flex-wrap"
                        style={{
                          maxWidth: "254px",
                          width: "100%",
                          gap: "15px 40px",
                        }}
                      >
                        <div className="legend-item">
                          <span
                            className="legend-color"
                            style={{ background: "#5ce0a0" }}
                          ></span>
                          <div className="d-flex flex-column">
                            <h4
                              className="text-body-b3 mb-0"
                              style={{ color: "#e6ecf1" }}
                            >
                              Journal
                            </h4>
                            <div
                              className="d-flex align-items-center"
                              style={{ gap: "6px" }}
                            >
                              <span
                                className="text-caption-c2"
                                style={{ color: "#abb0b4" }}
                              >
                                {powerStat.average_journals_percentage}%
                              </span>
                              <div
                                className="d-flex align-items-center"
                                style={{ gap: "6.82px" }}
                              >
                                <img
                                  src={
                                    parseFloat(
                                      powerStat.average_growth_journals_percentage,
                                    ) >= 0
                                      ? "/icons/assesssment-chart-increase.svg"
                                      : "/icons/assessment-chart-decrease.svg"
                                  }
                                  alt=""
                                />
                                <span
                                  className="text-caption-c2"
                                  style={{
                                    color:
                                      parseFloat(
                                        powerStat.average_growth_journals_percentage,
                                      ) >= 0
                                        ? "#5ce0a0"
                                        : "#ff4d4f",
                                  }}
                                >
                                  {powerStat.average_growth_journals_percentage}
                                  %
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="legend-item">
                          <span
                            className="legend-color"
                            style={{ background: "#66baff" }}
                          ></span>
                          <div className="d-flex flex-column">
                            <h4
                              className="text-body-b3 mb-0"
                              style={{ color: "#e6ecf1" }}
                            >
                              Habit
                            </h4>
                            <div
                              className="d-flex align-items-center"
                              style={{ gap: "6px" }}
                            >
                              <span
                                className="text-caption-c2"
                                style={{ color: "#abb0b4" }}
                              >
                                {powerStat.average_habits_percentage}%
                              </span>
                              <div
                                className="d-flex align-items-center"
                                style={{ gap: "6.82px" }}
                              >
                                <img
                                  src={
                                    parseFloat(
                                      powerStat.average_growth_habits_percentage,
                                    ) >= 0
                                      ? "/icons/assesssment-chart-increase.svg"
                                      : "/icons/assessment-chart-decrease.svg"
                                  }
                                  alt=""
                                />
                                <span
                                  className="text-caption-c2"
                                  style={{
                                    color:
                                      parseFloat(
                                        powerStat.average_growth_habits_percentage,
                                      ) >= 0
                                        ? "#5ce0a0"
                                        : "#ff4d4f",
                                  }}
                                >
                                  {powerStat.average_growth_habits_percentage}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="legend-item">
                          <span
                            className="legend-color"
                            style={{ background: "#f5ca41" }}
                          ></span>
                          <div className="d-flex flex-column">
                            <h4
                              className="text-body-b3 mb-0"
                              style={{ color: "#e6ecf1" }}
                            >
                              Task
                            </h4>
                            <div
                              className="d-flex align-items-center"
                              style={{ gap: "6px" }}
                            >
                              <span
                                className="text-caption-c2"
                                style={{ color: "#abb0b4" }}
                              >
                                {powerStat.average_tasks_percentage}%
                              </span>
                              <div
                                className="d-flex align-items-center"
                                style={{ gap: "6.82px" }}
                              >
                                <img
                                  src={
                                    parseFloat(
                                      powerStat.average_growth_tasks_percentage,
                                    ) >= 0
                                      ? "/icons/assesssment-chart-increase.svg"
                                      : "/icons/assessment-chart-decrease.svg"
                                  }
                                  alt=""
                                />
                                <span
                                  className="text-caption-c2"
                                  style={{
                                    color:
                                      parseFloat(
                                        powerStat.average_growth_tasks_percentage,
                                      ) >= 0
                                        ? "#5ce0a0"
                                        : "#ff4d4f",
                                  }}
                                >
                                  {powerStat.average_growth_tasks_percentage}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="legend-item">
                          <span
                            className="legend-color"
                            style={{ background: "#008cff" }}
                          ></span>
                          <div className="d-flex flex-column">
                            <h4
                              className="text-body-b3 mb-0"
                              style={{ color: "#e6ecf1" }}
                            >
                              Goals
                            </h4>
                            <div
                              className="d-flex align-items-center"
                              style={{ gap: "6px" }}
                            >
                              <span
                                className="text-caption-c2"
                                style={{ color: "#abb0b4" }}
                              >
                                {powerStat.average_goal_percentage}%
                              </span>
                              <div
                                className="d-flex align-items-center"
                                style={{ gap: "6.82px" }}
                              >
                                <img
                                  src={
                                    parseFloat(
                                      powerStat.average_growth_goal_percentage,
                                    ) >= 0
                                      ? "/icons/assesssment-chart-increase.svg"
                                      : "/icons/assessment-chart-decrease.svg"
                                  }
                                  alt=""
                                />
                                <span
                                  className="text-caption-c2"
                                  style={{
                                    color:
                                      parseFloat(
                                        powerStat.average_growth_goal_percentage,
                                      ) >= 0
                                        ? "#5ce0a0"
                                        : "#ff4d4f",
                                  }}
                                >
                                  {powerStat.average_growth_goal_percentage}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-12 col-xl-8">
                  <div
                    className="journal-card for-bar-chart p-3 p-md-4"
                    style={{ height: "415px", position: "relative" }}
                  >
                    <BarChart
                      datasets={barChartData}
                      labels={["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]}
                      maxValue={
                        powerStat &&
                        Math.max(
                          ...barChartData.reduce((sums, dataset) => {
                            dataset.data.forEach((value, index) => {
                              sums[index] = (sums[index] || 0) + value;
                            });
                            return sums;
                          }, []),
                        ) * 1.1
                      }
                      title="Average Score by Weekday"
                      containerStyle={{ height: "100%", width: "100%" }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="row">
                <div className="col-md-12">
                  <div
                    className="journal-card for-wave-chart p-3 p-md-4"
                    style={{ height: "610px" }}
                  >
                    <h2 className="text-heading-h3 mb-3 text-white">
                      Total Score by Month
                    </h2>
                    <div className="d-flex gap-3 gap-md-4 flex-wrap">
                      <div
                        className="legend-item text-body-b3"
                        style={{ gap: "12px" }}
                      >
                        <span
                          className="legend-color"
                          style={{ background: "#5ce0a0" }}
                        ></span>
                        Journal(AM/PM)
                      </div>
                      <div className="legend-item">
                        <span
                          className="legend-color"
                          style={{ background: "#66baff" }}
                        ></span>
                        Habit
                      </div>
                      <div className="legend-item">
                        <span
                          className="legend-color"
                          style={{ background: "#f5ca41" }}
                        ></span>
                        Task
                      </div>
                      <div className="legend-item">
                        <span
                          className="legend-color"
                          style={{ background: "#008cff" }}
                        ></span>
                        Goals
                      </div>
                    </div>
                    <div
                      className="chart-container for-wave mb-0"
                      style={{ height: "calc(100% - 70px)" }}
                    >
                      <LineChart
                        datasets={getLineChartData()}
                        labels={[
                          "J",
                          "F",
                          "M",
                          "A",
                          "M",
                          "J",
                          "J",
                          "A",
                          "S",
                          "O",
                          "N",
                          "D",
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
